import type { Session } from "@mux/sdk/v2/client"
import { createEffect, createRoot, runWithOwner, untrack, type Owner } from "solid-js"
import { createStore, reconcile, type SetStoreFunction, type Store } from "solid-js/store"
import { RevisionedCache, persisted } from "@/storage/persist"
import { sidebarSessionBoundary } from "@/utils/sidebar-session-boundary"
import { workspaceMatches } from "@/utils/workspace"
import { compareSessionActivity, rootSessionLoadTarget } from "./session-trim"
import { sessionRevision } from "./session-revision"
import { sessionSnoozedUntil } from "./session-snooze"
import { ROOT_SESSION_PAGE_LIMIT } from "./types"

export type SidebarSessionPageStatus = "idle" | "loading" | "ready" | "error"

type PersistedWorkspaceState = {
  value: Session[]
  hasMore?: boolean
  hasArchived?: boolean
  total?: number
}

type WorkspaceState = {
  expanded: boolean
  pageStatus: SidebarSessionPageStatus
  loadingMore: boolean
  nextCursor?: string
  limit: number
  archiveLoading: boolean
  archiveError: boolean
  archiveLoaded: boolean
}

type WorkspaceCache = {
  store: Store<PersistedWorkspaceState>
  setStore: SetStoreFunction<PersistedWorkspaceState>
  dispose: () => void
}

export function projectSidebarSessions(input: {
  directory: string
  loaded: boolean
  live: readonly Session[]
  cached: readonly Session[]
}) {
  const active = (sessions: readonly Session[]) => sessions.filter((session) => !session.time?.archived)
  if (input.loaded) return active(input.live).filter((session) => workspaceMatches(session, input.directory))
  // Before the list loads, each chat shows the newer of its two copies.
  const merged = new Map(input.cached.map((session) => [session.id, session]))
  for (const session of input.live) {
    if (!workspaceMatches(session, input.directory)) continue
    const cached = merged.get(session.id)
    if (!cached || sessionRevision(session) >= sessionRevision(cached)) merged.set(session.id, session)
  }
  return active([...merged.values()]).sort(compareSessionActivity)
}

export function needsSidebarSessionReplenish(input: {
  pageStatus: SidebarSessionPageStatus
  nextCursor?: string
  sessions: readonly Session[]
  limit: number
  pinnedIds: readonly string[]
}) {
  if (input.pageStatus !== "ready" || !input.nextCursor) return false
  const pinned = new Set(input.pinnedIds)
  const loadedRoots = input.sessions.filter(
    (session) => !session.parentID && !session.hidden && !session.time?.archived && !sessionSnoozedUntil(session) && !pinned.has(session.id),
  ).length
  return loadedRoots < rootSessionLoadTarget(input.limit)
}

export function createSidebarSessionStore(input: {
  owner: Owner
  serverKey: () => string
  sessionsForWorkspace: (directory: string) => readonly Session[]
  pinnedSessionIds: () => readonly string[]
  sessionTotal: (directory: string) => number | undefined
  replenish: (directory: string) => Promise<void>
  fetchArchive: (directory: string) => Promise<Session[]>
  mergeArchived: (directory: string, sessions: Session[]) => void
}) {
  const caches = new Map<string, WorkspaceCache>()
  const [workspaces, setWorkspaces] = createStore<Record<string, WorkspaceState>>({})
  let loadSessions: ((directory: string, options?: { more?: boolean; force?: boolean }) => Promise<void>) | undefined

  function cacheSessions(cache: WorkspaceCache, sessions: readonly Session[], limit: number, hasMore?: boolean) {
    const roots = sessions
      .filter((session) => !session.parentID && !session.hidden && !session.time?.archived)
      .sort(compareSessionActivity)
    const boundary = [
      ...sidebarSessionBoundary(roots.filter((session) => !sessionSnoozedUntil(session)), limit, input.pinnedSessionIds()),
      ...roots.filter((session) => sessionSnoozedUntil(session) > 0),
    ]
    cache.setStore("value", reconcile(boundary, { key: "id" }))
    const cachedHasMore = roots.length > boundary.length ? true : hasMore
    if (cachedHasMore !== undefined && cachedHasMore !== untrack(() => cache.store.hasMore)) {
      cache.setStore("hasMore", cachedHasMore)
    }
  }

  function ensure(directory: string) {
    if (!directory) throw new Error("A workspace ID is required for sidebar session state")
    let cache = caches.get(directory)
    if (!cache) {
      setWorkspaces(directory, {
        expanded: false,
        pageStatus: "idle",
        loadingMore: false,
        nextCursor: undefined,
        limit: ROOT_SESSION_PAGE_LIMIT,
        archiveLoading: false,
        archiveError: false,
        archiveLoaded: false,
      })
      cache = runWithOwner(input.owner, () =>
        createRoot((dispose) => {
          const created = persisted(
            RevisionedCache.workspace(directory, `sidebar_sessions:${input.serverKey() || "no-server"}`),
            createStore<PersistedWorkspaceState>({ value: [] }),
          )
          const current: WorkspaceCache = { store: created[0], setStore: created[1], dispose }
          createEffect(() => {
            const total = input.sessionTotal(directory)
            if (total !== undefined) current.setStore("total", Math.max(0, total))
          })
          createEffect(() => {
            const state = workspaces[directory]
            if (state?.pageStatus !== "ready") return
            cacheSessions(current, input.sessionsForWorkspace(directory), state.limit, state.nextCursor !== undefined)
          })
          createEffect(() => {
            const hasArchived = input.sessionsForWorkspace(directory)
              .some((session) => !session.parentID && !session.hidden && !!session.time?.archived)
            if (hasArchived || workspaces[directory]?.archiveLoaded) {
              current.setStore("hasArchived", hasArchived)
            }
          })
          createEffect(() => {
            const state = workspaces[directory]
            const sessions = input.sessionsForWorkspace(directory)
            const pinnedIds = input.pinnedSessionIds()
            if (!state || !needsSidebarSessionReplenish({
              pageStatus: state.pageStatus,
              nextCursor: state.nextCursor,
              sessions,
              limit: state.limit,
              pinnedIds,
            })) return
            void input.replenish(directory).catch(() => undefined)
          })
          return current
        }),
      )
      if (!cache) {
        setWorkspaces(directory, undefined!)
        throw new Error("Failed to create sidebar session cache")
      }
      caches.set(directory, cache)
    }
    return { cache, state: workspaces[directory]! }
  }

  function sessions(directory: string) {
    const { cache, state } = ensure(directory)
    return projectSidebarSessions({
      directory,
      loaded: state.pageStatus === "ready" || state.loadingMore,
      live: input.sessionsForWorkspace(directory),
      cached: cache.store.value,
    })
  }

  function page(directory: string) {
    return ensure(directory).state
  }

  function replace(directory: string, sessions: Session[], hasMore?: boolean, hasArchived?: boolean) {
    const { cache, state } = ensure(directory)
    cacheSessions(cache, sessions, state.limit, hasMore)
    if (hasArchived !== undefined) cache.setStore("hasArchived", hasArchived)
  }

  function setPage(directory: string, patch: Partial<WorkspaceState>) {
    ensure(directory)
    setWorkspaces(directory, patch)
  }

  function hasMore(directory: string) {
    return ensure(directory).cache.store.hasMore === true
  }

  function needsReplenish(directory: string) {
    const state = ensure(directory).state
    return needsSidebarSessionReplenish({
      pageStatus: state.pageStatus,
      nextCursor: state.nextCursor,
      sessions: input.sessionsForWorkspace(directory),
      limit: state.limit,
      pinnedIds: input.pinnedSessionIds(),
    })
  }

  function total(directory: string) {
    return ensure(directory).cache.store.total
  }

  function archived(directory: string) {
    ensure(directory)
    return input.sessionsForWorkspace(directory)
      .filter((session) => !session.parentID && !!session.time?.archived)
      .sort((a, b) =>
        (b.time?.archived ?? 0) - (a.time?.archived ?? 0) ||
        (b.time?.updated ?? 0) - (a.time?.updated ?? 0),
      )
  }

  function hasArchived(directory: string) {
    const { cache } = ensure(directory)
    const live = input.sessionsForWorkspace(directory)
      .some((session) => !session.parentID && !session.hidden && !!session.time?.archived)
    return live || cache.store.hasArchived === true
  }

  function archiveLoaded(directory: string) {
    return workspaces[directory]?.archiveLoaded ?? false
  }

  function archiveLoading(directory: string) {
    return workspaces[directory]?.archiveLoading ?? false
  }

  function archiveError(directory: string) {
    return workspaces[directory]?.archiveError ?? false
  }

  async function loadArchive(directory: string) {
    if (archiveLoading(directory)) return
    const cache = ensure(directory).cache
    setPage(directory, { archiveLoading: true, archiveError: false })
    try {
      const sessions = await input.fetchArchive(directory)
      if (caches.get(directory) === cache) {
        input.mergeArchived(directory, sessions)
        cache.setStore("hasArchived", input.sessionsForWorkspace(directory)
          .some((session) => !session.parentID && !session.hidden && !!session.time?.archived))
        setPage(directory, { archiveLoaded: true })
      }
    } catch {
      if (caches.get(directory) === cache) setPage(directory, { archiveError: true })
    } finally {
      if (caches.get(directory) === cache) setPage(directory, { archiveLoading: false })
    }
  }
  function expanded(directory: string) {
    return workspaces[directory]?.expanded ?? false
  }

  function setExpanded(directory: string, value: boolean) {
    setPage(directory, { expanded: value })
  }

  function dispose(directory: string) {
    const cache = caches.get(directory)
    if (!cache) return
    caches.delete(directory)
    cache.dispose()
    setWorkspaces(directory, undefined!)
  }

  function disposeAll() {
    for (const directory of [...caches.keys()]) dispose(directory)
  }

  return {
    bindLoader(loader: typeof loadSessions) {
      loadSessions = loader
    },
    load(directory: string, options?: { more?: boolean; force?: boolean }) {
      if (!loadSessions) throw new Error("Sidebar session loader is not configured")
      return loadSessions(directory, options)
    },
    loadMore(directory: string) {
      if (!loadSessions) throw new Error("Sidebar session loader is not configured")
      return loadSessions(directory, { more: true })
    },
    sessions,
    page,
    setPage,
    replace,
    hasMore,
    needsReplenish,
    total,
    archived,
    hasArchived,
    archiveLoaded,
    archiveLoading,
    archiveError,
    loadArchive,
    expanded,
    setExpanded,
    dispose,
    disposeAll,
  }
}
